import { Static, Type } from '@sinclair/typebox'
import { AiBuilderText } from './ai-builder-text'
import { AiBuilderCardType } from './cards'
import { AI_BUILDER_PROMPT_HARD_CAP } from './payload-limits'

/**
 * Which card a `card_reply` input answers: type, step and (for a dropdown)
 * prop, and a field choice's sub-field key — the fields every card schema (`cards.ts`) already carries.
 */
export const CardTarget = Type.Object({
    type: AiBuilderCardType,
    stepName: Type.String(),
    propName: Type.Optional(Type.String()),
    /** A field choice's dynamic sub-field key */
    fieldKey: Type.Optional(Type.String()),
})
export type CardTarget = Static<typeof CardTarget>

/**
 * A turn's one input, typed at the source: a button, a card answer, a help
 * request, a resume or free text — never a bracketed string the server has
 * to re-parse.
 */
export const AiBuilderTurnInput = Type.Union([
    Type.Object({ type: Type.Literal('text'), text: Type.String({ maxLength: AI_BUILDER_PROMPT_HARD_CAP }) }),
    Type.Object({ type: Type.Literal('step_done') }),
    /**
     * The walker's own "Need help?" (no `about`) or the panel's "Need help?"
     * on a specific step (`about: {type:'step', stepName}`) — both start a
     * `conversation{help_on_step}` (`core/situation.ts`).
     */
    Type.Object({ type: Type.Literal('need_help') }),
    Type.Object({ type: Type.Literal('need_more_help') }),
    Type.Object({ type: Type.Literal('resume') }),
    Type.Object({
        type: Type.Literal('card_reply'),
        card: CardTarget,
        value: Type.Unknown(),
        /** The chosen option's display label — the user's words when the reply cannot be matched and falls back to free text */
        label: Type.String({ maxLength: AI_BUILDER_PROMPT_HARD_CAP }),
    }),
])
export type AiBuilderTurnInput = Static<typeof AiBuilderTurnInput>

/**
 * A quick-reply button: what it shows (a key or literal text) and what it
 * sends. For a `type: 'text'` send, `send.text` carries the KEY, translated
 * at dispatch time by `sendInputFor` (front, `ai-builder-utils.ts`): the
 * server cannot know the user's language.
 */
export const SuggestedAction = Type.Object({
    label: AiBuilderText,
    send: AiBuilderTurnInput,
})
export type SuggestedAction = Static<typeof SuggestedAction>
